/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { identity, pickBy } from 'lodash';
import { CrudListType, CrudRemoveType, CrudTemplate, ListColumns } from '../../../organisms/crud/form-types';
import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import { TFunction } from 'react-i18next';
import { Alarm, Medication } from 'app/types/medication';
import React from 'react';
import { DateTime } from 'app/components/atoms/date-time/date-time';

/**
 * List labels.
 */

const listColumns = (translate: TFunction): ListColumns<Medication> => [
  {
    title: translate('common.table.columns.id'),
    dataIndex: 'id',
    key: 'id',
    render: (item: Medication) => `${item.id.slice(0, 4)}...${item.id.slice(-4)}`
  },
  {
    title: translate('common.table.columns.drug'),
    dataIndex: 'drug',
    size: '1fr',
    key: 'drug',
    render: (item: Medication) => JSON.parse(item.drug.productName).pt
  },
  {
    title: translate('common.table.columns.frequency'),
    dataIndex: 'frequency',
    key: 'frequency',
    render: (item: Medication) => item.frequency
  },
  {
    title: translate('common.table.columns.startTreatment'),
    dataIndex: 'startTreatment',
    key: 'startTreatment',
    render: (item: Medication) => <DateTime date={item.startTreatment} />
  },
  {
    title: translate('common.table.columns.endTreatment'),
    dataIndex: 'endTreatment',
    key: 'endTreatment',
    render: (item: Medication) => <DateTime date={item.endTreatment} />
  },
  {
    title: translate('common.table.columns.activeDays'),
    dataIndex: 'activeDays',
    key: 'activeDays',
    render: (item: Medication) => item.activeDays
  },
  {
    title: translate('common.table.columns.pauseDays'),
    dataIndex: 'pauseDays',
    key: 'pauseDays',
    render: (item: Medication) => item.pauseDays
  },
  {
    title: translate('common.table.columns.weekDays'),
    dataIndex: 'weekDays',
    key: 'weekDays',
    render: (item: Medication) => item.weekDays
  },
  {
    title: translate('common.table.columns.treatmentDays'),
    dataIndex: 'treatmentDays',
    key: 'treatmentDays',
    render: (item: Medication) => item.treatmentDays
  },
  {
    title: translate('common.table.columns.pills'),
    dataIndex: 'pills',
    key: 'pills',
    render: (item: Medication) => item.pills
  },
  {
    title: translate('common.table.columns.orderItemId'),
    dataIndex: 'orderItemId',
    key: 'orderItemId',
    render: (item: Medication) => item.orderItemId
  },
  {
    title: translate('common.table.columns.createdAt'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: (item: Medication) => <DateTime date={item.createdAt} />
  },
  {
    title: translate('common.table.columns.updatedAt'),
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    render: (item: Medication) => <DateTime date={item.updatedAt} />
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: translate('common.labels.search')
  },
  {
    type: 'selectSearch',
    name: 'accessType',
    inline: true,
    label: translate('common.labels.accessType'),
    showSearch: true,
    allowClear: true,
    options: [
      {
        label: translate('common.labels.user'),
        value: 'user'
      },
      {
        label: translate('common.labels.backOffice'),
        value: 'backOffice'
      },
      {
        label: translate('common.labels.admin'),
        value: 'admin'
      }
    ]
  }
];

/**
 * List.
 */

function list(translate: TFunction, extendedProposalsTemplate: CrudTemplate<Alarm>): CrudListType<Medication> {
  return {
    columns: listColumns(translate),
    filters: listFilters(translate),
    route: appRoutes.dashboard.users.detail.medications,
    enableExport: false,
    expandedTemplate: extendedProposalsTemplate as any,
    addButton: translate('common.actions.add'),
    canEdit: true,
    canRemove: true,
    resolveParams: (params: any, item?: Medication) => {
      return {
        userId: params.userId,
        deviceId: params.id ?? item?.id
      };
    },
    key: ['userMedication'],
    normalizeFilters: (data: any) => {
      const { ...filters } = data.filters;
      const result: any = {
        filters: pickBy(filters, identity),
        order: []
      };

      return result;
    },
    normalizeData: (result: any) => {
      return result;
    },
    pagination: {
      pageSize: 25,
      pageSizeOptions: [10, 25, 50, 100, 500, 1000]
    },
    endpoint: apiEndpoints.userMedications,
    endpointById: apiEndpoints.userMedicationsById
  };
}

/**
 * Remove.
 */

const remove = (): CrudRemoveType<Medication> => ({
  resolveParams: (params: any, item?: Medication) => {
    return {
      userId: params.userId,
      id: params.id ?? item?.id
    };
  },
  endpoint: apiEndpoints.userMedicationsById
});

/**
 * Config.
 */

export function createUserMedicationsTemplate(
  translate: TFunction,
  extendedProposalsTemplate: CrudTemplate<Alarm>
): CrudTemplate<Medication> {
  return {
    list: list(translate, extendedProposalsTemplate),
    remove: remove()
  };
}
